import * as FavoritesUpdatedEvent from '@ori-events/favorites-updated';
import { ButtonBase, styled } from '@ori-ui/mui';
import { HeartsAnimation } from '@ori/favorites-heart';
import { usePersonalBeautyStore } from '@ori/personal-beauty-store';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useEffect, useState, useCallback } from 'react';

import { useAppConfig } from '../../context';
import { useToggleMenu } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations';
import { BURGER_MENU } from './constants';

const BurgerMenuIconRoot = styled(ButtonBase)<{ ownerState?: { isOpen?: boolean } }>(({ ownerState, theme }) => ({
  color: theme.palette.grey.A700,
  fontSize: theme.typography.pxToRem(28),
  height: 32,
  marginRight: theme.spacing(3),
  padding: '0 0 0 3px',

  [theme.breakpoints.up('sm')]: {
    marginRight: theme.spacing(6),
  },

  [theme.breakpoints.up('md')]: {
    '&:hover': {
      '.burgerMenuIconLine': {
        height: 3,
      },
    },

    ...(ownerState?.isOpen && {
      '&:hover': {
        '.burgerMenuIconLine': {
          height: 2,
        },
      },
    }),
  },

  [theme.breakpoints.up('lg')]: {
    marginRight: theme.spacing(8),
  },
}));

const BurgerMenuIconWrapper = styled('div')<{
  ownerState?: { isOpen?: boolean; isMobile?: boolean };
}>(({ ownerState, theme }) => ({
  display: 'none',

  ...(ownerState?.isMobile && {
    display: 'block',
  }),

  [theme.breakpoints.up('md')]: {
    display: 'block',

    ...(ownerState?.isMobile && {
      display: 'none',
    }),
  },
}));

const BurgerMenuIconLine = styled('div')<{
  ownerState?: { isOpen?: boolean; isMobile?: boolean };
}>(({ ownerState, theme }) => ({
  backgroundColor: theme.palette.grey.A700,
  height: 2,
  margin: '6px 0',
  opacity: 1,
  position: 'relative',
  transform: 'translateX(0) rotate(0) translateY(0)',
  transition: 'opacity 0.3s linear, transform 0.3s linear',
  width: 24,

  ...(ownerState?.isMobile && {
    display: 'block',
  }),

  [theme.breakpoints.up('md')]: {
    width: 25,
    ...(ownerState?.isOpen && {
      '&:nth-of-type(1)': {
        transform: 'rotate(45deg) translateY(9px) translateX(2px)',
      },

      '&:nth-of-type(2)': {
        opacity: 0,
        transform: 'translateX(22px)',
      },

      '&:nth-of-type(3)': {
        transform: 'rotate(-45deg) translateY(-9px) translateX(2px);',
      },
    }),
  },
}));

export const BurgerMenuIcon: FC = () => {
  const [menuIsOpen, toggleMenu] = useToggleMenu();
  const [wasFavoriteAdded, setWasFavoriteAdded] = useState(false);
  const {
    tooltipWasShown: pbsTooltipWasShown,
    setTooltipWasShown: setPbsTooltipWasShown,
    pbsData,
  } = usePersonalBeautyStore();

  const { pbsIconInBurgerMenu } = useAppConfig();
  const { formatMessage } = useTranslations();
  const ownerState = { isOpen: menuIsOpen };
  const { getTestId } = useTestId();

  const onClickCallback = useCallback(() => {
    const { firstName, lastName } = pbsData?.beautyStore?.profile ?? {};

    if (!pbsTooltipWasShown && pbsIconInBurgerMenu && firstName && lastName) {
      setPbsTooltipWasShown(true);
    }

    toggleMenu();
  }, [pbsTooltipWasShown, toggleMenu, setPbsTooltipWasShown, pbsIconInBurgerMenu, pbsData?.beautyStore?.profile]);

  const onFavoritesUpdated = ({ detail: { favorites, lastUpdated } }: FavoritesUpdatedEvent.Event) => {
    if (lastUpdated && favorites.includes(lastUpdated)) {
      setWasFavoriteAdded(() => true);

      setTimeout(() => {
        setWasFavoriteAdded(() => false);
      }, 0);
    }
  };

  useEffect(() => {
    window.addEventListener(FavoritesUpdatedEvent.Name, onFavoritesUpdated as EventListener);

    return () => {
      window.removeEventListener(FavoritesUpdatedEvent.Name, onFavoritesUpdated as EventListener);
    };
  }, []);

  return (
    <BurgerMenuIconRoot
      disableRipple
      aria-label={formatMessage('menu')}
      ownerState={ownerState}
      data-testid={getTestId(BURGER_MENU)}
      onClick={onClickCallback}
    >
      <BurgerMenuIconWrapper ownerState={{ ...ownerState, isMobile: true }}>
        <HeartsAnimation
          shouldAnimationRun
          state={wasFavoriteAdded}
        >
          <BurgerMenuIconLine
            ownerState={ownerState}
            className="burgerMenuIconLine"
          />
          <BurgerMenuIconLine
            ownerState={ownerState}
            className="burgerMenuIconLine"
          />
          <BurgerMenuIconLine
            ownerState={ownerState}
            className="burgerMenuIconLine"
          />
        </HeartsAnimation>
      </BurgerMenuIconWrapper>
      <BurgerMenuIconWrapper>
        <BurgerMenuIconLine
          ownerState={ownerState}
          className="burgerMenuIconLine"
        />
        <BurgerMenuIconLine
          ownerState={ownerState}
          className="burgerMenuIconLine"
        />
        <BurgerMenuIconLine
          ownerState={ownerState}
          className="burgerMenuIconLine"
        />
      </BurgerMenuIconWrapper>
    </BurgerMenuIconRoot>
  );
};
